import { useTheme } from "../../../../hooks/useTheme";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../../Containers/PopupModalContainer";
import { Colors } from "../../../../utils/colors";
import RectangleButton from "../../../Buttons/RectangleButton";
import { useCallback, useMemo, useState } from "react";
import { ConversationPreset, NotificationType } from "@markit/common.types";
import {
  detectedCensored,
  makeEmptyConversationPreset,
} from "@markit/common.utils";
import { generate } from "shortid";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import CustomTextFieldMultiline from "../../../TextFields/CustomTextFieldMultiline";
import { API } from "../../../../API";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../redux/store";
import { showNotificationBanner } from "../../../../utils/notificationUtils";
import ContentViolationTagModal from "../../../DeliveryScore/ContentViolationTagModal";
import isEqual from "lodash.isequal";
import { conversationActions } from "../../../../redux/slices/conversationSlice";

type ConversationPresetModalProps = {
  closeOnPress: () => void;
  existingPreset?: ConversationPreset;
};

const ConversationPresetModal = (props: ConversationPresetModalProps) => {
  const { closeOnPress, existingPreset } = props;
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const uid = useSelector((state: AppState) => state.account.accountData.uid);
  const [conversationPreset, setConversationPreset] =
    useState<ConversationPreset>(
      existingPreset || makeEmptyConversationPreset(generate())
    );
  const [isSaving, setIsSaving] = useState(false);

  const madeChanges = useMemo(
    () =>
      !existingPreset ||
      (existingPreset && !isEqual(conversationPreset, existingPreset)),
    [conversationPreset, existingPreset]
  );

  const saveConversationPreset = useCallback(async () => {
    setIsSaving(true);
    await API.conversation.createConversationPreset({
      userId: uid,
      conversationPreset,
    });
    if (existingPreset) {
      dispatch(
        conversationActions.updateConversationPreset(conversationPreset)
      );
    } else {
      dispatch(conversationActions.addConversationPreset(conversationPreset));
    }
    setIsSaving(false);
    showNotificationBanner(
      dispatch,
      "Preset Created",
      NotificationType.AFFIRMATIVE
    );
    closeOnPress();
  }, [uid, conversationPreset, dispatch, closeOnPress, existingPreset]);

  return (
    <PopupModalContainer
      valueComp={
        <div className="ColumnNormal" style={{ gap: 14 }}>
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <span className="bodyMedium">
              {existingPreset ? "Edit" : "Create"} Text Preset
            </span>
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <div
                className="descriptionTextAreaContainer"
                style={{ height: 160, backgroundColor: Colors.TRANSPARENT }}
              >
                <CustomTextFieldMultiline
                  value={conversationPreset.preset}
                  placeholder={"Add your preset here..."}
                  onChange={(e) => {
                    setConversationPreset({
                      ...conversationPreset,
                      preset: e.target.value,
                    });
                  }}
                  maxLength={1000}
                  containerStyles={{
                    height: "100%",
                    border: "none",
                    borderRadius: 0,
                    padding: 0,
                    backgroundColor: Colors.TRANSPARENT,
                  }}
                />
                <div className="AlignedRowSpaced" style={{ marginTop: 7 }}>
                  <span className="smallBodySubtext">
                    {conversationPreset.preset.length}/1000
                  </span>
                  {detectedCensored(conversationPreset.preset) ? (
                    <ContentViolationTagModal
                      message={conversationPreset.preset}
                      numCredits={0}
                      theme={theme}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      }
      fixedBottomContent={
        <div className="ColumnNormal">
          <HorizontalDivider altMargin={14} />
          <div className="AlignedRow" style={{ gap: 10, paddingInline: 14 }}>
            <RectangleButton
              buttonLabel="Cancel"
              onPress={closeOnPress}
              altTextColor={Colors.BLACK}
              altColor={Colors.WHITE1}
              containerStyles={{
                padding: "14px 12px",
                border: `0.5px solid ${Colors.GRAY2}`,
              }}
            />
            <RectangleButton
              buttonLabel={existingPreset ? "Save" : "Create"}
              onPress={saveConversationPreset}
              altTextColor={Colors.WHITE}
              altColor={Colors.BLACK}
              containerStyles={{ padding: "14px 12px" }}
              disabled={
                detectedCensored(conversationPreset.preset) ||
                !conversationPreset.preset.trim() ||
                !madeChanges
              }
              loading={isSaving}
            />
          </div>
        </div>
      }
      noExit
      noDivider
      closeModal={closeOnPress}
      theme={theme}
      modalContentWidth={PopupModalContainerWidth.SMALL}
    />
  );
};

export default ConversationPresetModal;
